import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getFooterConfigAdmin, updateFooterConfigAdmin } from '../../../api/cabsHomepageAdmin';

const emptySection = () => ({ title: '', links: [{ label: '', url: '' }] });

const AdminCabsFooter = () => {
  const { data } = useQuery({
    queryKey: ['cabs-footer-admin'],
    queryFn: getFooterConfigAdmin,
  });
  const [form, setForm] = useState({
    sections: [emptySection()],
    places: [''],
    contact: { email: '', phone: '', address: '' },
    socials: { instagram: '', facebook: '', x: '', youtube: '', linkedin: '' },
    newsletterEyebrow: '',
    newsletterHeading: '',
    newsletterSubheading: '',
    newsletterButtonLabel: '',
    newsletterPlaceholder: '',
    copyrightText: '',
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const cfg = data?.data || data;
    if (cfg) {
      setForm({
        sections: cfg.sections || [emptySection()],
        places: Array.isArray(cfg.places) && cfg.places.length ? cfg.places : [''],
        contact: cfg.contact || { email: '', phone: '', address: '' },
        socials: cfg.socials || { instagram: '', facebook: '', x: '', youtube: '', linkedin: '' },
        newsletterEyebrow: cfg.newsletterEyebrow || '',
        newsletterHeading: cfg.newsletterHeading || '',
        newsletterSubheading: cfg.newsletterSubheading || '',
        newsletterButtonLabel: cfg.newsletterButtonLabel || '',
        newsletterPlaceholder: cfg.newsletterPlaceholder || '',
        copyrightText: cfg.copyrightText || '',
      });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload) => updateFooterConfigAdmin(payload),
    onSuccess: () => setMessage('Footer updated.'),
    onError: (err) => setMessage(err.message || 'Failed to update footer.'),
  });

  const updateSection = (idx, patch) => {
    setForm((prev) => {
      const next = [...prev.sections];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, sections: next };
    });
  };

  const updateLink = (sectionIdx, linkIdx, patch) => {
    setForm((prev) => {
      const nextSections = [...prev.sections];
      const links = [...(nextSections[sectionIdx]?.links || [])];
      links[linkIdx] = { ...links[linkIdx], ...patch };
      nextSections[sectionIdx] = { ...nextSections[sectionIdx], links };
      return { ...prev, sections: nextSections };
    });
  };

  const addSection = () => setForm((prev) => ({ ...prev, sections: [...prev.sections, emptySection()] }));
  const addLink = (idx) =>
    setForm((prev) => {
      const next = [...prev.sections];
      next[idx] = { ...next[idx], links: [...(next[idx].links || []), { label: '', url: '' }] };
      return { ...prev, sections: next };
    });

  const updatePlace = (idx, value) => {
    setForm((prev) => {
      const next = [...(prev.places || [])];
      next[idx] = value;
      return { ...prev, places: next };
    });
  };

  const addPlace = () => setForm((prev) => ({ ...prev, places: [...(prev.places || []), ''] }));

  const removePlace = (idx) => {
    setForm((prev) => {
      const next = (prev.places || []).filter((_, index) => index !== idx);
      return { ...prev, places: next.length ? next : [''] };
    });
  };

  const save = () => {
    const cleanedPlaces = (form.places || []).map((place) => place.trim()).filter(Boolean);
    mutation.mutate({ ...form, places: cleanedPlaces });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">Cab Services</p>
        <h1 className="text-2xl font-semibold text-slate-900">Footer</h1>
        <p className="text-sm text-slate-600">Shared footer configuration for the public site.</p>
      </div>

      {message && <div className="alert alert-info">{message}</div>}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Footer sections</h2>
        {form.sections.map((section, idx) => (
          <div key={idx} className="rounded-xl border border-slate-200 p-3 space-y-3">
            <input
              className="form-control"
              placeholder="Section title"
              value={section.title}
              onChange={(e) => updateSection(idx, { title: e.target.value })}
            />
            {(section.links || []).map((link, linkIdx) => (
              <div key={linkIdx} className="row g-2">
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Label"
                    value={link.label}
                    onChange={(e) => updateLink(idx, linkIdx, { label: e.target.value })}
                  />
                </div>
                <div className="col-md-8">
                  <input
                    className="form-control"
                    placeholder="URL"
                    value={link.url}
                    onChange={(e) => updateLink(idx, linkIdx, { url: e.target.value })}
                  />
                </div>
              </div>
            ))}
            <button className="btn btn-outline-secondary btn-sm" onClick={() => addLink(idx)}>
              Add link
            </button>
          </div>
        ))}
        <button className="btn btn-outline-primary btn-sm" onClick={addSection}>
          Add section
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Contact</h2>
        <input
          className="form-control"
          placeholder="Email"
          value={form.contact.email || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, contact: { ...prev.contact, email: e.target.value } }))}
        />
        <input
          className="form-control"
          placeholder="Phone"
          value={form.contact.phone || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, contact: { ...prev.contact, phone: e.target.value } }))}
        />
        <textarea
          className="form-control"
          rows="2"
          placeholder="Address"
          value={form.contact.address || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, contact: { ...prev.contact, address: e.target.value } }))}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Social links</h2>
        {Object.keys(form.socials).map((key) => (
          <input
            key={key}
            className="form-control"
            placeholder={key}
            value={form.socials[key] || ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, socials: { ...prev.socials, [key]: e.target.value } }))
            }
          />
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Places (Cities served)</h2>
        <div className="space-y-2">
          {(form.places || []).map((place, idx) => (
            <div key={`place-${idx}`} className="row g-2 align-items-center">
              <div className="col">
                <input
                  className="form-control"
                  placeholder="City name"
                  value={place}
                  onChange={(e) => updatePlace(idx, e.target.value)}
                />
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => removePlace(idx)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <button className="btn btn-outline-primary btn-sm" onClick={addPlace}>
          Add place
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Newsletter</h2>
        <input
          className="form-control"
          placeholder="Eyebrow text"
          value={form.newsletterEyebrow || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, newsletterEyebrow: e.target.value }))}
        />
        <input
          className="form-control"
          placeholder="Heading"
          value={form.newsletterHeading || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, newsletterHeading: e.target.value }))}
        />
        <input
          className="form-control"
          placeholder="Subheading"
          value={form.newsletterSubheading || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, newsletterSubheading: e.target.value }))}
        />
        <input
          className="form-control"
          placeholder="Button label"
          value={form.newsletterButtonLabel || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, newsletterButtonLabel: e.target.value }))}
        />
        <input
          className="form-control"
          placeholder="Input placeholder"
          value={form.newsletterPlaceholder || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, newsletterPlaceholder: e.target.value }))}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-2">
        <h2 className="text-lg font-semibold text-slate-900">Copyright</h2>
        <input
          className="form-control"
          placeholder="Copyright text"
          value={form.copyrightText || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, copyrightText: e.target.value }))}
        />
      </div>

      <button className="btn btn-primary" disabled={mutation.isPending} onClick={save}>
        {mutation.isPending ? 'Saving...' : 'Save footer'}
      </button>
    </div>
  );
};

export default AdminCabsFooter;
